/**
 * Purchasing Workspace — Coolman Operations Platform (DEC-044 · v1).
 *
 * Implements the first operational version of Purchasing Intelligence
 * authorised by DEC-044, honouring the approved Business Discovery for
 * PIC-103 · B2.1 (Days of Cover) and B2.2 (Tone Thresholds).
 *
 * Workspace tabs (per Product Owner directive 2026-02-04):
 *   • Dashboard              — where should I look first?
 *   • Purchasing Intelligence — the fuel-by-fuel operational view
 *   • Recommendations        — placeholder (future work)
 *   • Thresholds             — business-policy editor (audit-versioned)
 *   • History                — versioned snapshots (why did this change?)
 *
 * Design principles preserved from B2.1/B2.2:
 *   • Advisory only. No "Order Now" button. Attention, not action.
 *   • Confidence and Data Quality shown separately.
 *   • Every threshold change captures who · when · previous · new · reason.
 *   • Plain-language reasoning first; numbers as evidence.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Gauge, ChartBar, Sliders, ClockCounterClockwise, Lightbulb,
  ArrowsClockwise, WarningCircle, CheckCircle, MagnifyingGlass,
  Pencil, Info, CurrencyDollar,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import DailyPricingTab from "@/pages/purchasing/DailyPricingTab";
import RecommendationsTab from "@/pages/purchasing/RecommendationsTab";

/* ================================================================== */
/*  API                                                                */
/* ================================================================== */
const piApi = {
  products:          ()          => api.get("/purchasing-intelligence/products"),
  productDetail:     (code)      => api.get(`/purchasing-intelligence/products/${code}`),
  thresholds:        ()          => api.get("/purchasing-intelligence/thresholds"),
  updateThreshold:   (code, b)   => api.put(`/purchasing-intelligence/thresholds/${code}`, b),
  thresholdHistory:  (code)      => api.get(`/purchasing-intelligence/thresholds/${code}/history`),
  snapshots:         (params)    => api.get("/purchasing-intelligence/snapshots", { params }),
};

/* ================================================================== */
/*  Visual atoms                                                       */
/* ================================================================== */
const TONE_STYLES = {
  green: {
    chip: "bg-emerald-100 text-emerald-800 border-emerald-300",
    ring: "ring-emerald-400/40",
    dot:  "bg-emerald-500",
    label: "Green",
  },
  amber: {
    chip: "bg-amber-100 text-amber-900 border-amber-300",
    ring: "ring-amber-400/40",
    dot:  "bg-amber-500",
    label: "Amber",
  },
  red: {
    chip: "bg-rose-100 text-rose-800 border-rose-300",
    ring: "ring-rose-400/40",
    dot:  "bg-rose-500",
    label: "Red",
  },
};
const CONFIDENCE_STYLES = {
  higher:   { chip: "bg-slate-800 text-white",        label: "Higher confidence" },
  moderate: { chip: "bg-slate-200 text-slate-800",    label: "Moderate confidence" },
  lower:    { chip: "bg-slate-100 text-slate-600 border border-slate-300", label: "Lower confidence" },
};
const DATA_QUALITY_STYLES = {
  high:     { chip: "bg-sky-100 text-sky-800 border-sky-300",       label: "High data quality" },
  moderate: { chip: "bg-yellow-100 text-yellow-800 border-yellow-300", label: "Moderate data quality" },
  low:      { chip: "bg-orange-100 text-orange-800 border-orange-300", label: "Low data quality" },
};
const ATTENTION_STYLES = {
  needs_attention: {
    chip: "bg-rose-600 text-white",
    label: "Needs Attention",
    hint: "Where to look first",
  },
  monitor: {
    chip: "bg-amber-500 text-white",
    label: "Monitor",
    hint: "Worth keeping an eye on",
  },
  stable: {
    chip: "bg-emerald-600 text-white",
    label: "Stable",
    hint: "Routine attention only",
  },
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d)) return "—";
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return "—"; }
}
function fmtRelative(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.round(diff/60)}m ago`;
    if (diff < 86400) return `${Math.round(diff/3600)}h ago`;
    return `${Math.round(diff/86400)}d ago`;
  } catch { return "—"; }
}

/* ================================================================== */
/*  Product Intelligence Card                                          */
/* ================================================================== */
function IntelligenceCard({ item, onOpenDetail }) {
  const tone = TONE_STYLES[item.tone] || TONE_STYLES.amber;
  const conf = CONFIDENCE_STYLES[item.confidence?.tier] || CONFIDENCE_STYLES.moderate;
  const dq   = DATA_QUALITY_STYLES[item.data_quality?.tier] || DATA_QUALITY_STYLES.moderate;
  const att  = ATTENTION_STYLES[item.management_attention] || ATTENTION_STYLES.monitor;

  return (
    <article
      data-testid={`pi-card-${item.product_code}`}
      className={`rounded-xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition ring-1 ${tone.ring}`}
    >
      <header className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">
            {item.product_code}
          </div>
          <h3 className="text-lg font-semibold text-slate-900" data-testid={`pi-card-title-${item.product_code}`}>
            {item.product_label}
          </h3>
        </div>
        <span
          data-testid={`pi-attention-${item.product_code}`}
          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${att.chip}`}
          title={att.hint}
        >
          {att.label}
        </span>
      </header>

      <div className="mt-4 flex items-baseline gap-2">
        <div className="text-3xl font-bold text-slate-900" data-testid={`pi-doc-${item.product_code}`}>
          {item.days_of_cover != null ? item.days_of_cover.toFixed(1) : "—"}
        </div>
        <div className="text-sm text-slate-500">days of cover</div>
        <span
          className={`ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${tone.chip}`}
          data-testid={`pi-tone-${item.product_code}`}
        >
          <span className={`h-2 w-2 rounded-full ${tone.dot}`} />
          {tone.label}
        </span>
      </div>

      <p className="mt-3 text-sm text-slate-700 leading-snug" data-testid={`pi-headline-${item.product_code}`}>
        {item.explanation?.headline || "—"}
      </p>

      <div className="mt-4 grid grid-cols-2 gap-2 text-[11px]">
        <div>
          <div className="text-slate-500 uppercase tracking-wide">Inventory</div>
          <div className="text-slate-800 font-medium">
            {item.inventory?.units != null ? `${Number(item.inventory.units).toLocaleString()} L` : "—"}
          </div>
          <div className="text-slate-500">{fmtRelative(item.inventory?.ts)}</div>
        </div>
        <div>
          <div className="text-slate-500 uppercase tracking-wide">Consumption</div>
          <div className="text-slate-800 font-medium">
            {item.consumption?.litres_per_day != null
              ? `${Number(item.consumption.litres_per_day).toLocaleString()} L/day`
              : "—"}
          </div>
          <div className="text-slate-500">{fmtRelative(item.consumption?.ts)}</div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${conf.chip}`}
              data-testid={`pi-confidence-${item.product_code}`}>
          {conf.label}
        </span>
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${dq.chip}`}
              data-testid={`pi-dataquality-${item.product_code}`}>
          {dq.label}
        </span>
      </div>

      <footer className="mt-4 flex items-center justify-between">
        <button
          type="button"
          onClick={() => onOpenDetail(item.product_code)}
          data-testid={`pi-open-detail-${item.product_code}`}
          className="text-xs font-semibold text-slate-700 hover:text-slate-900 inline-flex items-center gap-1"
        >
          <MagnifyingGlass size={14} weight="bold" /> Explain
        </button>
        <div className="text-[10px] text-slate-400">Advisory · not a decision</div>
      </footer>
    </article>
  );
}

/* ================================================================== */
/*  Detail Drawer                                                      */
/* ================================================================== */
function DetailDrawer({ code, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    if (!code) return;
    setLoading(true);
    try {
      const { data } = await piApi.productDetail(code);
      setDetail(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load detail");
    } finally { setLoading(false); }
  }, [code]);
  useEffect(() => { load(); }, [load]);

  if (!code) return null;
  const tone = TONE_STYLES[detail?.tone] || TONE_STYLES.amber;
  const att  = ATTENTION_STYLES[detail?.management_attention] || ATTENTION_STYLES.monitor;
  const conf = CONFIDENCE_STYLES[detail?.confidence?.tier] || CONFIDENCE_STYLES.moderate;
  const dq   = DATA_QUALITY_STYLES[detail?.data_quality?.tier] || DATA_QUALITY_STYLES.moderate;

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/40" onClick={onClose} data-testid="pi-detail-overlay">
      <aside
        className="absolute top-0 right-0 h-full w-full max-w-lg bg-white shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="pi-detail-drawer"
      >
        <header className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between">
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wide">{detail?.product_code}</div>
            <h2 className="text-xl font-semibold text-slate-900">{detail?.product_label || "…"}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="pi-detail-close"
            className="text-slate-500 hover:text-slate-800 text-sm font-medium"
          >Close</button>
        </header>

        {loading || !detail ? (
          <div className="p-6 text-sm text-slate-500">Loading…</div>
        ) : (
          <div className="p-6 space-y-6">
            <section className="space-y-2">
              <div className="flex items-baseline gap-2">
                <div className="text-4xl font-bold text-slate-900">
                  {detail.days_of_cover != null ? detail.days_of_cover.toFixed(1) : "—"}
                </div>
                <div className="text-sm text-slate-500">days of cover</div>
                <span className={`ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${tone.chip}`}>
                  <span className={`h-2 w-2 rounded-full ${tone.dot}`} />
                  {tone.label}
                </span>
              </div>
              <p className="text-slate-800">{detail.explanation?.headline}</p>
              <div className="flex flex-wrap gap-2">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${att.chip}`}>
                  {att.label}
                </span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${conf.chip}`}>
                  {conf.label}
                </span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${dq.chip}`}>
                  {dq.label}
                </span>
              </div>
            </section>

            <ExplanationBlock explanation={detail.explanation} />

            <section>
              <h3 className="text-sm font-semibold text-slate-800 mb-2">Business evidence</h3>
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <div><dt className="text-slate-500 text-xs uppercase">Inventory</dt>
                     <dd className="text-slate-900">{detail.inventory?.units != null ? `${Number(detail.inventory.units).toLocaleString()} L` : "—"}</dd>
                     <dd className="text-slate-500 text-xs">{fmtDateTime(detail.inventory?.ts)} · {detail.inventory?.source || "—"}</dd></div>
                <div><dt className="text-slate-500 text-xs uppercase">Consumption</dt>
                     <dd className="text-slate-900">{detail.consumption?.litres_per_day != null ? `${Number(detail.consumption.litres_per_day).toLocaleString()} L/day` : "—"}</dd>
                     <dd className="text-slate-500 text-xs">{detail.consumption?.method?.replaceAll("_"," ") || "—"} · horizon {detail.consumption?.horizon_days ?? "—"}d</dd></div>
                <div><dt className="text-slate-500 text-xs uppercase">Threshold (business policy)</dt>
                     <dd className="text-slate-900">🟢 ≥ {detail.threshold?.green_min_days?.toFixed(0)}d · 🔴 &lt; {detail.threshold?.red_max_days?.toFixed(0)}d</dd></div>
                <div><dt className="text-slate-500 text-xs uppercase">Confidence dimensions</dt>
                     <dd className="text-slate-900 text-xs">
                       C {detail.confidence?.dimensions?.completeness} ·
                       F {detail.confidence?.dimensions?.freshness} ·
                       P {detail.confidence?.dimensions?.provenance} ·
                       Coh {detail.confidence?.dimensions?.coherence}
                     </dd></div>
              </dl>
            </section>

            <section className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-xs text-slate-700">
              <div className="font-semibold text-slate-800 mb-1 flex items-center gap-1">
                <Info size={14} weight="bold" /> Governance
              </div>
              {detail.explanation?.governance_notice}
            </section>
          </div>
        )}
      </aside>
    </div>
  );
}

function ExplanationBlock({ explanation }) {
  if (!explanation) return null;
  const Q = explanation.questions || {};
  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold text-slate-800">Explanation</h3>
      <ExplainRow n="1" title="What operational situation is currently being represented?" body={Q.q1_what} />
      <ExplainRow n="2" title="Why does the current business information support that understanding?" body={Q.q2_why} />
      <ExplainRow n="3" title="What factors most significantly influenced the operational understanding?" list={Q.q3_factors} />
      <ExplainRow n="4" title="What uncertainty or limitations should management recognise before acting?" list={Q.q4_limitations} />
      {explanation.attention_guidance ? (
        <p className="rounded-md bg-slate-100 text-slate-800 p-3 text-sm">{explanation.attention_guidance}</p>
      ) : null}
    </section>
  );
}
function ExplainRow({ n, title, body, list }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Q{n}</div>
      <div className="text-sm font-medium text-slate-800">{title}</div>
      {body ? <div className="mt-1 text-sm text-slate-700">{body}</div> : null}
      {Array.isArray(list) ? (
        <ul className="mt-1 list-disc list-inside text-sm text-slate-700 space-y-0.5">
          {list.map((it, i) => <li key={i}>{it}</li>)}
        </ul>
      ) : null}
    </div>
  );
}

/* ================================================================== */
/*  Thresholds Editor                                                  */
/* ================================================================== */
function ThresholdsTab({ canWrite }) {
  // DEC-051 · Threshold Administration consolidates all purchasing policy
  // into one workspace at /admin/thresholds. This tab now redirects there
  // rather than duplicating the tuning UI in two places.
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 text-center" data-testid="thresholds-redirect">
      <p className="text-sm text-slate-700">
        Threshold tuning has moved to a single administration workspace so managers have one authoritative
        place to configure purchasing engine policy.
      </p>
      <a href="/admin/thresholds" data-testid="thresholds-redirect-link"
         className="mt-4 inline-flex items-center gap-2 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2">
        Open Threshold Administration →
      </a>
    </div>
  );
}

/* eslint-disable no-unused-vars */
function _LegacyThresholdsTab({ canWrite }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);       // product_code
  const [historyFor, setHistoryFor] = useState(null); // product_code
  const [historyRows, setHistoryRows] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await piApi.thresholds();
      setRows(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load thresholds");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openHistory = async (code) => {
    setHistoryFor(code);
    setHistoryRows([]);
    try {
      const { data } = await piApi.thresholdHistory(code);
      setHistoryRows(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load history");
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900" data-testid="thresholds-policy-banner">
        <div className="font-semibold mb-0.5">Business Policy — not mathematical precision.</div>
        Thresholds express management's operational tolerance for inventory risk. Adjust them when the operational
        characteristics of a product change materially — not in response to short-term fluctuations. Every change is
        recorded with who · when · previous · new · reason.
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Product</th>
                <th className="px-4 py-2 text-right font-medium">🟢 Green ≥</th>
                <th className="px-4 py-2 text-right font-medium">🔴 Red &lt;</th>
                <th className="px-4 py-2 text-left font-medium">Last updated</th>
                <th className="px-4 py-2 text-left font-medium">By</th>
                <th className="px-4 py-2 text-left font-medium">Reason</th>
                <th className="px-4 py-2"/>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.product_code} className="border-t border-slate-100" data-testid={`threshold-row-${r.product_code}`}>
                  <td className="px-4 py-2 font-medium text-slate-800">{r.product_code}</td>
                  <td className="px-4 py-2 text-right">{Number(r.green_min_days).toFixed(0)}d</td>
                  <td className="px-4 py-2 text-right">{Number(r.red_max_days).toFixed(0)}d</td>
                  <td className="px-4 py-2 text-slate-600">{fmtDateTime(r.updated_at)}</td>
                  <td className="px-4 py-2 text-slate-600">{r.updated_by}</td>
                  <td className="px-4 py-2 text-slate-600 max-w-[24ch] truncate" title={r.reason || ""}>
                    {r.seed ? <span className="italic text-slate-500">engineering-seeded default</span> : (r.reason || "—")}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => openHistory(r.product_code)}
                      data-testid={`threshold-history-${r.product_code}`}
                      className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
                      title="Show change history"
                    >
                      <ClockCounterClockwise size={14} /> History
                    </button>
                    {canWrite ? (
                      <button
                        type="button"
                        onClick={() => setEditing(r)}
                        data-testid={`threshold-edit-${r.product_code}`}
                        className="ml-3 text-xs text-slate-800 hover:text-slate-950 font-semibold inline-flex items-center gap-1"
                      >
                        <Pencil size={14} /> Edit
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing ? (
        <ThresholdEditModal
          current={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      ) : null}
      {historyFor ? (
        <HistoryModal
          code={historyFor}
          rows={historyRows}
          onClose={() => { setHistoryFor(null); setHistoryRows([]); }}
        />
      ) : null}
    </div>
  );
}

function ThresholdEditModal({ current, onClose, onSaved }) {
  const [green, setGreen] = useState(current.green_min_days);
  const [red, setRed]     = useState(current.red_max_days);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const disabled = saving || !(Number(green) > Number(red));

  const save = async () => {
    setSaving(true);
    try {
      await piApi.updateThreshold(current.product_code, {
        green_min_days: Number(green),
        red_max_days: Number(red),
        reason: reason || null,
      });
      toast.success(`Thresholds updated for ${current.product_code}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update thresholds");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 grid place-items-center" onClick={onClose} data-testid="threshold-edit-overlay">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4" onClick={(e)=>e.stopPropagation()} data-testid="threshold-edit-modal">
        <div>
          <div className="text-xs text-slate-500 uppercase">{current.product_code}</div>
          <h2 className="text-lg font-semibold text-slate-900">Edit Tone Thresholds</h2>
          <p className="text-xs text-slate-600 mt-1">These express business policy for this product. Adjust when operational characteristics have <em>materially</em> changed.</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm">
            <span className="block text-slate-600 mb-1">🟢 Green ≥ (days)</span>
            <input type="number" min={0.5} step={0.5} value={green}
                   onChange={(e)=>setGreen(e.target.value)}
                   data-testid="threshold-edit-green"
                   className="w-full rounded border border-slate-300 px-2 py-1"/>
          </label>
          <label className="text-sm">
            <span className="block text-slate-600 mb-1">🔴 Red &lt; (days)</span>
            <input type="number" min={0.5} step={0.5} value={red}
                   onChange={(e)=>setRed(e.target.value)}
                   data-testid="threshold-edit-red"
                   className="w-full rounded border border-slate-300 px-2 py-1"/>
          </label>
        </div>
        {!(Number(green) > Number(red)) ? (
          <p className="text-xs text-rose-700">Green threshold must be greater than Red threshold.</p>
        ) : null}
        <label className="text-sm block">
          <span className="block text-slate-600 mb-1">Reason (optional but recommended)</span>
          <textarea rows={3} value={reason} onChange={(e)=>setReason(e.target.value)}
                    placeholder="e.g. Tightening operational tolerance after supplier lead-time review."
                    data-testid="threshold-edit-reason"
                    className="w-full rounded border border-slate-300 px-2 py-1"/>
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose}
                  data-testid="threshold-edit-cancel"
                  className="text-sm px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50">Cancel</button>
          <button type="button" onClick={save} disabled={disabled}
                  data-testid="threshold-edit-save"
                  className="text-sm px-3 py-1.5 rounded bg-slate-900 text-white hover:bg-slate-800 disabled:bg-slate-400">
            {saving ? "Saving…" : "Save policy change"}
          </button>
        </div>
      </div>
    </div>
  );
}

function HistoryModal({ code, rows, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 grid place-items-center" onClick={onClose} data-testid="history-overlay">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6 space-y-3" onClick={(e)=>e.stopPropagation()} data-testid="history-modal">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Threshold history — {code}</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900 text-sm">Close</button>
        </div>
        {rows.length === 0 ? (
          <p className="text-sm text-slate-500">No changes recorded yet.</p>
        ) : (
          <ul className="space-y-2 max-h-[60vh] overflow-y-auto">
            {rows.map((h) => (
              <li key={h.id} className="rounded border border-slate-200 p-3 text-sm">
                <div className="text-xs text-slate-500">{fmtDateTime(h.changed_at)} · {h.changed_by}</div>
                <div className="mt-1 text-slate-800">
                  <span className="text-slate-500">🟢 </span>
                  {h.previous?.green_min_days ?? "—"}d → <b>{h.new?.green_min_days}d</b>
                  <span className="text-slate-500 ml-4">🔴 </span>
                  {h.previous?.red_max_days ?? "—"}d → <b>{h.new?.red_max_days}d</b>
                </div>
                {h.reason ? <div className="mt-1 text-slate-600 text-xs">Reason: {h.reason}</div> : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ================================================================== */
/*  History Tab (snapshots)                                            */
/* ================================================================== */
function HistoryTab({ products }) {
  const [selected, setSelected] = useState(products?.[0]?.product_code || "");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const { data } = await piApi.snapshots({ product_code: selected, limit: 100 });
      setRows(data.items || []);
    } catch (e) { toast.error("Failed to load snapshots"); }
    finally { setLoading(false); }
  }, [selected]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selected && products?.length) setSelected(products[0].product_code);
  }, [products, selected]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <label className="text-sm text-slate-700">Product</label>
        <select value={selected} onChange={(e) => setSelected(e.target.value)}
                data-testid="history-product-select"
                className="text-sm rounded border border-slate-300 px-2 py-1">
          {(products || []).map((p) => (
            <option key={p.product_code} value={p.product_code}>
              {`${p.product_label} (${p.product_code})`}
            </option>
          ))}
        </select>
      </div>
      <p className="text-xs text-slate-600">Each row is a versioned Purchasing Intelligence snapshot. This history is the seed of the future <em>"why did this change since yesterday?"</em> capability.</p>
      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500">No snapshots yet for this product.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">Computed</th>
                <th className="px-3 py-2 text-right">DoC</th>
                <th className="px-3 py-2 text-left">Tone</th>
                <th className="px-3 py-2 text-left">Confidence</th>
                <th className="px-3 py-2 text-left">Data Quality</th>
                <th className="px-3 py-2 text-left">Attention</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => {
                const tone = TONE_STYLES[s.tone] || TONE_STYLES.amber;
                return (
                  <tr key={s.id} className="border-t border-slate-100" data-testid={`snapshot-row-${s.id}`}>
                    <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{fmtDateTime(s.computed_at)}</td>
                    <td className="px-3 py-2 text-right font-medium">{s.days_of_cover != null ? s.days_of_cover.toFixed(1) : "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${tone.chip}`}>
                        <span className={`h-2 w-2 rounded-full ${tone.dot}`} /> {tone.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 capitalize">{s.confidence?.tier || "—"}</td>
                    <td className="px-3 py-2 capitalize">{s.data_quality?.tier || "—"}</td>
                    <td className="px-3 py-2">{ATTENTION_STYLES[s.management_attention]?.label || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/*  Dashboard                                                          */
/* ================================================================== */
function DashboardTab({ items }) {
  const counts = useMemo(() => {
    const c = { needs_attention: 0, monitor: 0, stable: 0 };
    (items || []).forEach((it) => { c[it.management_attention] = (c[it.management_attention] || 0) + 1; });
    return c;
  }, [items]);

  const tile = (key) => {
    const style = ATTENTION_STYLES[key];
    return (
      <div key={key} className={`rounded-xl p-5 shadow-sm border border-slate-200 bg-white`}>
        <div className="text-xs uppercase tracking-wide text-slate-500">{style.hint}</div>
        <div className="mt-2 flex items-baseline gap-2">
          <div className="text-4xl font-bold text-slate-900" data-testid={`dashboard-count-${key}`}>{counts[key] || 0}</div>
          <div className={`px-2 py-0.5 rounded-full text-xs font-medium ${style.chip}`}>{style.label}</div>
        </div>
      </div>
    );
  };

  const priorityList = (items || []).filter((i) => i.management_attention === "needs_attention");

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {tile("needs_attention")}
        {tile("monitor")}
        {tile("stable")}
      </div>
      <section>
        <h3 className="text-sm font-semibold text-slate-800 mb-2">Where to look first</h3>
        {priorityList.length === 0 ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-center gap-2">
            <CheckCircle size={16} weight="fill" /> Nothing currently needs immediate attention.
          </div>
        ) : (
          <ul className="space-y-2">
            {priorityList.map((it) => (
              <li key={it.product_code} className="rounded-lg border border-slate-200 bg-white p-4 flex items-center gap-3" data-testid={`dashboard-priority-${it.product_code}`}>
                <WarningCircle className="text-rose-500" size={20} weight="fill" />
                <div className="flex-1">
                  <div className="font-semibold text-slate-900">{it.product_label}</div>
                  <div className="text-sm text-slate-700">{it.explanation?.headline}</div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-slate-900">{it.days_of_cover != null ? it.days_of_cover.toFixed(1) : "—"}</div>
                  <div className="text-[10px] text-slate-500">days of cover</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/* ================================================================== */
/*  Root Page                                                          */
/* ================================================================== */
const TABS = [
  { id: "dashboard",    label: "Dashboard",              icon: Gauge },
  { id: "intelligence", label: "Purchasing Intelligence", icon: ChartBar },
  { id: "daily_pricing", label: "Daily Pricing",         icon: CurrencyDollar },
  { id: "recommendations", label: "Recommendations",      icon: Lightbulb },
  { id: "thresholds",   label: "Thresholds",             icon: Sliders },
  { id: "history",      label: "History",                icon: ClockCounterClockwise },
];

export default function Purchasing() {
  const { user } = useAuth();
  const canWrite = ["Administrator", "Owner"].includes(user?.role);

  const [tab, setTab] = useState("dashboard");
  const [items, setItems] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailCode, setDetailCode] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await piApi.products();
      setItems(data.items || []);
      setGeneratedAt(data.generated_at);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Purchasing Intelligence");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 space-y-6" data-testid="purchasing-workspace">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wide">Purchasing</div>
          <h1 className="text-2xl font-semibold text-slate-900">Purchasing Workspace</h1>
          <p className="text-sm text-slate-600 max-w-2xl mt-1">
            Advisory operational intelligence. <span className="font-medium">Inform management — never replace management.</span>
          </p>
        </div>
        <div className="text-right text-xs text-slate-500">
          <div>Generated {fmtRelative(generatedAt)}</div>
          <button
            type="button"
            onClick={load}
            data-testid="purchasing-refresh"
            className="mt-1 inline-flex items-center gap-1 text-slate-700 hover:text-slate-900 font-medium"
          >
            <ArrowsClockwise size={12} /> Refresh
          </button>
        </div>
      </header>

      <nav className="flex flex-wrap gap-1 border-b border-slate-200" data-testid="purchasing-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-t-md border-b-2 ${
              tab === t.id
                ? "border-slate-900 text-slate-900 font-semibold"
                : "border-transparent text-slate-600 hover:text-slate-900"
            }`}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </nav>

      <section>
        {tab === "dashboard" ? (
          <DashboardTab items={items} />
        ) : tab === "intelligence" ? (
          loading ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-slate-500">No products available.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pi-grid">
              {items.map((it) => (
                <IntelligenceCard key={it.product_code} item={it} onOpenDetail={setDetailCode} />
              ))}
            </div>
          )
        ) : tab === "daily_pricing" ? (
          <DailyPricingTab canWrite={canWrite} />
        ) : tab === "recommendations" ? (
          <RecommendationsTab canWrite={canWrite} />
        ) : tab === "thresholds" ? (
          <ThresholdsTab canWrite={canWrite} />
        ) : (
          <HistoryTab products={items} />
        )}
      </section>

      {detailCode ? (
        <DetailDrawer code={detailCode} onClose={() => setDetailCode(null)} />
      ) : null}
    </div>
  );
}
